import Component from '@glimmer/component'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type { QuickStartResourceViewModel } from 'teacher-ui-client/subscription-type/quick-start/template.gts'
import { t } from 'ember-intl'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import { service } from '@ember/service'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'

export enum QuickStartLabel {
  writingLessons = 'Writing Lessons',
  excitingWritingIdeas = 'Exciting Writing Ideas',
  lessons = 'Lessons',
  longWrites = 'Long Writes',
  drivingTestsAndReports = 'Driving Tests and Reports',
  fastPhonics = 'Fast Phonics',
  libraryJournalAndReadAloudFluency = 'Library, Journal and Read Aloud Fluency',
  mathseedsPrime = 'Mathseeds Prime',
  mentalMinute = 'Mental Minute',
  placementTest = 'Placement Test',
  readingEggs = 'Reading Eggs',
  readingEggspress2To6 = 'Reading Eggspress 2-6',
  reportsLexileGrowthAndCertificates = 'Reports, Lexile Growth and Certificates',
  spellingLessons = 'Spelling Lessons',
}

interface Signature {
  Element: HTMLDivElement
  Args: {
    contentfulQuickStartResource: QuickStartResourceViewModel
    matchesTeacherIntent?: boolean
  }
}

export class QuickStartCard extends Component<Signature> {
  @service teacherEvents!: TeacherEvents

  // Arbitrarily set classes for each label, have just tried to make labels for the same product spread across different colors
  get labelClasses(): string {
    switch (this.args.contentfulQuickStartResource.label) {
      case QuickStartLabel.writingLessons:
      case QuickStartLabel.mentalMinute:
      case QuickStartLabel.reportsLexileGrowthAndCertificates:
      default:
        return 'bg-3E-pink-150 text-purply-pink-350'
      case QuickStartLabel.excitingWritingIdeas:
      case QuickStartLabel.libraryJournalAndReadAloudFluency:
      case QuickStartLabel.readingEggspress2To6:
      case QuickStartLabel.mathseedsPrime:
        return 'bg-grapey-green-100 text-green-400'
      case QuickStartLabel.lessons:
      case QuickStartLabel.readingEggs:
      case QuickStartLabel.placementTest:
      case QuickStartLabel.spellingLessons:
        return 'bg-juicy-orange-100 text-juicy-orange-500'
      case QuickStartLabel.longWrites:
      case QuickStartLabel.drivingTestsAndReports:
      case QuickStartLabel.fastPhonics:
        return 'bg-rexy-purple-50 text-rexy-purple-300'
    }
  }

  handleClick = (type: 'video' | 'pdf', event: MouseEvent) => {
    const href = (event.currentTarget as HTMLAnchorElement).href
    this.teacherEvents.sendEvent('quick-start-resource-clicked', this.args.contentfulQuickStartResource.product, {
      resourceLabel: this.args.contentfulQuickStartResource.label,
      resourceType: type,
      resourceUrl: href,
    })
  }
  <template>
    <div class="flex max-w-3xl overflow-clip rounded-2xl border-2 border-[#0092FA]" data-test-quick-start-card>
      <div class="flex basis-[calc(100%/7*4)] flex-col justify-between gap-6 p-6">
        <div class="space-y-3">
          <h2 class="text-2xl font-medium">{{@contentfulQuickStartResource.heading}}</h2>
          <div
            class="{{this.labelClasses}} w-fit rounded-sm px-2 py-1 text-sm"
          >{{@contentfulQuickStartResource.label}}</div>
          <p> {{@contentfulQuickStartResource.body}} </p>
        </div>
        <div class="flex flex-wrap gap-x-6 gap-y-4 [&>*:not(:only-child)]:flex-1">
          {{#if @contentfulQuickStartResource.videoUrl}}
            <UiButtonLink
              href={{@contentfulQuickStartResource.videoUrl}}
              class="flex items-center gap-1 text-neutral-300"
              target="_blank"
              rel="noopener noreferrer"
              {{on "click" (fn this.handleClick "video")}}
            >
              <PhosphorIcon @icon="phosphor-play-circle-fill" class="h-6 w-6 shrink-0" />
              <p class="text-left">{{t "quickStartResource.video"}}</p>
            </UiButtonLink>
          {{/if}}
          {{#if @contentfulQuickStartResource.pdfUrl}}
            <UiButtonLink
              href={{@contentfulQuickStartResource.pdfUrl}}
              class="flex items-center gap-1 text-neutral-300"
              target="_blank"
              rel="noopener noreferrer"
              {{on "click" (fn this.handleClick "pdf")}}
            >
              <PhosphorIcon @icon="phosphor-file-text-fill" class="h-6 w-6 shrink-0" />
              <p class="text-left">{{t "quickStartResource.pdf"}}</p>
            </UiButtonLink>
          {{/if}}

        </div>
      </div>
      <div class="basis-[calc(100%/7*3)] space-y-4 bg-linear-to-b from-[#CFF8FF] to-[#D1DFF4] p-4">
        <div class="flex h-10 w-full items-center justify-end">
          {{#if @matchesTeacherIntent}}<span
              class="bg-yolky-yellow-300 flex h-10 w-10 items-center justify-center rounded-full shadow-md"
            ><PhosphorIcon @icon="phosphor-star-fill" class="h-7 w-7 text-white" /></span>{{/if}}
        </div>
        <ul class="space-y-4">
          {{#each @contentfulQuickStartResource.bullets as |bullet|}}
            <li>
              <PhosphorIcon @icon="phosphor-circle-fill" class="mr-1 inline-block h-2 w-2 text-[#0092FA]" />
              <span>
                {{bullet}}
              </span>
            </li>
          {{/each}}
        </ul>
      </div>
    </div>
  </template>
}
export default QuickStartCard
